import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getList, deleteOne } from "../../dataProvider/API";
import { toast } from "react-toastify";

import { requestJson, buildApiUrl } from "../../dataProvider/requestJson";
import { ALERT_TEXT } from "../../constants/titles";

// The Venues list itself is not in Redux: the page reads `admin/venues` through
// `src/utils/venueAdminApi.js` and keeps its filters in the URL (DIVE-6877).
const initialState = {
  loading: false,
  fetchDataShortVenues: [],
};

export const getVenuesShort = createAsyncThunk(
  "venues/getVenuesShort",
  ({ endpoint }) => getList(endpoint),
);

export const deleteVenue = createAsyncThunk("venues/deleteVenue", (endpoint) =>
  deleteOne(endpoint),
);

/**
 * Create/update go through requestJson rather than the legacy dataProvider so the
 * backend's reason reaches the user.
 *
 * The address timezone is now validated server-side and rejects with a plain-text 400
 * ("Invalid IANA timezone: +05:00"). fetchJson calls JSON.parse on every body regardless
 * of status, so that response surfaced as a SyntaxError and the toast showed only the
 * generic "request has been failed" — hiding the one message the admin can act on.
 */
export const createVenue = createAsyncThunk(
  "venues/create",
  async ({ endpoint, data }, { rejectWithValue }) => {
    try {
      return await requestJson(buildApiUrl(endpoint), {
        method: "POST",
        body: JSON.stringify(data),
      });
    } catch (error) {
      return rejectWithValue(error?.message || ALERT_TEXT.BAD_REQUEST);
    }
  },
);

export const updateVenue = createAsyncThunk(
  "venues/update",
  async ({ endpoint, data }, { rejectWithValue }) => {
    try {
      return await requestJson(buildApiUrl(endpoint), {
        method: "POST",
        body: JSON.stringify(data),
      });
    } catch (error) {
      return rejectWithValue(error?.message || ALERT_TEXT.BAD_REQUEST);
    }
  },
);

export const venuesSlice = createSlice({
  name: "venues",
  initialState,
  extraReducers: {
    [deleteVenue.pending]: (state) => {
      state.loading = true;
    },
    [deleteVenue.fulfilled]: (state) => {
      state.loading = false;
      toast.success(ALERT_TEXT.SUCCESS_DELETE_VENUE);
    },
    [deleteVenue.rejected]: (state) => {
      state.loading = false;
      toast.error(ALERT_TEXT.BAD_REQUEST);
    },
    [getVenuesShort.fulfilled]: (state, action) => {
      state.fetchDataShortVenues = action.payload;
    },
    [createVenue.fulfilled]: () => {
      toast.success(ALERT_TEXT.SUCCESS_CREATE_VENUE);
    },
    // action.payload carries the backend's message (e.g. an invalid timezone).
    [createVenue.rejected]: (state, action) => {
      toast.error(action.payload || ALERT_TEXT.BAD_REQUEST);
    },
    [updateVenue.fulfilled]: () => {
      toast.success(ALERT_TEXT.SUCCESS_UPDATE_VENUE);
    },
    [updateVenue.rejected]: (state, action) => {
      toast.error(action.payload || ALERT_TEXT.BAD_REQUEST);
    },
  },
});

export default venuesSlice.reducer;
